/**
 * El timbre de dos notas de la tarjeta de invitación. BEST-EFFORT por diseño (spec §4.2):
 * el `AudioContext` se crea en el primer `pointerdown`/`keydown` de la sesión (los mismos
 * oyentes pasivos que ya mide `api.ts`) y se guarda; al sonar, si no está `running` se intenta
 * `resume()`. Si sigue suspendido (pestaña abierta por link o navegada por el service worker sin
 * clic posterior) no suena, y quien llama cae a título parpadeante + Notification.
 */
let ctx: AudioContext | null = null;
let armed = false;

function ensureContext(): AudioContext | null {
  if (ctx) return ctx;
  try { ctx = new AudioContext(); } catch { ctx = null; }
  return ctx;
}

/** Instalar una sola vez: crea el contexto en el primer gesto real del usuario. */
export function armChime(): void {
  if (armed || typeof window === 'undefined') return;
  armed = true;
  const onGesture = () => {
    const c = ensureContext();
    if (c && c.state !== 'running') void c.resume().catch(() => {});
  };
  window.addEventListener('pointerdown', onGesture, { passive: true, capture: true });
  window.addEventListener('keydown', onGesture, { passive: true, capture: true });
}

/** Suena dos notas (0,6 s). Devuelve false si el navegador no dejó (sin gesto previo). */
export async function playChime(): Promise<boolean> {
  const c = ensureContext();
  if (!c) return false;
  if (c.state !== 'running') {
    try { await c.resume(); } catch { return false; }
    // TS cree que resume() siempre deja 'running'; el navegador no: sin gesto sigue 'suspended'.
    if ((c.state as AudioContextState) !== 'running') return false;
  }
  try {
    const t0 = c.currentTime;
    const master = c.createGain();
    master.gain.value = 0.0001;
    master.connect(c.destination);
    const nota = (freq: number, at: number, dur: number) => {
      const o = c.createOscillator();
      const g = c.createGain();
      o.type = 'sine';
      o.frequency.value = freq;
      g.gain.setValueAtTime(0.0001, at);
      g.gain.exponentialRampToValueAtTime(0.35, at + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, at + dur);
      o.connect(g).connect(master);
      o.start(at);
      o.stop(at + dur + 0.05);
    };
    master.gain.setValueAtTime(1, t0);
    nota(659.25, t0, 0.28);        // E5
    nota(880, t0 + 0.3, 0.3);      // A5
    return true;
  } catch { return false; }
}
